import type { AdminCrewReportUploadColumnDefinition } from "../models/admin-crew-report.models";
import type {
  AdminCrewReportImportItemOutcome,
  AdminCrewReportMonthsOnBoardBucketKey,
  AdminCrewReportOwnerRepatriationSegment,
  AdminCrewReportRepatriationVesselCountKey,
  AdminCrewReportRequestLifecycleStatus,
  AdminCrewReportRequestLifecycleStatusLabel,
  AdminCrewReportStatisticsView,
} from "../models/admin-crew-report.models";

export const ADMIN_CREW_REPORT_UPLOAD_MAX_ROWS = 100;
export const ADMIN_CREW_REPORT_ACTIONS_FORM_ID = "admin-crew-report-actions-form";
export const ADMIN_CREW_REPORT_STATISTICS_GRID_ID = "admin-crew-report-statistics-grid";
export const ADMIN_CREW_REPORT_STATISTICS_PDF_EXPORT_LABEL = "(PDF Export)";
export const ADMIN_CREW_REPORT_STATISTICS_PRINT_BODY_CLASS = "admin-crew-report-statistics-printing";
export const ADMIN_CREW_REPORT_STATISTICS_PRINT_PAGE_STYLE_ID = "admin-crew-report-statistics-print-page-style";
export const ADMIN_CREW_REPORT_STATISTICS_PRINT_PREPARATION_DELAY_MS = 0;
export const ADMIN_CREW_REPORT_STATISTICS_PRINT_CLEANUP_TIMEOUT_MS = 60_000;
export const ADMIN_CREW_REPORT_STATISTICS_PRINT_PAGE_STYLE = `
@page {
  size: A4 landscape;
  margin: 6mm 10mm;
}
`;
export const ADMIN_CREW_REPORT_IMPORT_CONTINUATION_MESSAGE =
  "The candidate limit was reached. Remaining eligible attachments will resume on the next import cycle.";
export const ADMIN_CREW_REPORT_IMPORT_INTERRUPTED_MESSAGE =
  "Completed batch results were preserved. Run the import again to continue with the remaining work.";
export const ADMIN_CREW_REPORT_IMPORT_PDF_DOCUMENT_TITLE_PREFIX = "crew-report-import-summary";
export const ADMIN_CREW_REPORT_IMPORT_PDF_TIMESTAMP_MILLISECONDS_PATTERN = /\.\d{3}Z$/;
export const ADMIN_CREW_REPORT_IMPORT_PDF_TIMESTAMP_SEPARATORS_PATTERN = /[-:]/g;
export const ADMIN_CREW_REPORT_IMPORT_PRINT_BODY_CLASS = "admin-crew-report-import-printing";
export const ADMIN_CREW_REPORT_IMPORT_PRINT_PAGE_STYLE_ID = "admin-crew-report-import-print-page-style";
export const ADMIN_CREW_REPORT_IMPORT_PRINT_PAGE_STYLE = `
@page {
  size: A4 portrait;
  margin: 10mm;
}
`;
export const ADMIN_CREW_REPORT_IMPORT_OUTCOME_CLASSES: Record<AdminCrewReportImportItemOutcome, string> = {
  IMPORTED: "bg-[#E7F5F2] text-green dark:bg-teal-950/50 dark:text-teal-200",
  FAILED: "bg-[#FDECEC] text-[#BF0002] dark:bg-red-950/40 dark:text-red-200",
  RETRYABLE_FAILURE: "bg-[#FDECEC] text-[#BF0002] dark:bg-red-950/40 dark:text-red-200",
  SUPERSEDED: "bg-amber-50 text-[#8A5A00] dark:bg-amber-950/40 dark:text-amber-200",
  ALREADY_PROCESSED: "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200",
};
export const ADMIN_CREW_REPORT_MONTHS_ON_BOARD_BUCKET_LABELS: Record<AdminCrewReportMonthsOnBoardBucketKey, string> = {
  BELOW_8_MONTHS: "Below 8m",
  FROM_8_TO_11_MONTHS: "8m to 11m",
  FROM_11_TO_13_MONTHS: "11m to 13m",
  ABOVE_13_MONTHS: "Above 13m",
};
export const ADMIN_CREW_REPORT_REQUEST_STATUS_LABELS: Record<
  AdminCrewReportRequestLifecycleStatus,
  AdminCrewReportRequestLifecycleStatusLabel
> = {
  NOT_REQUESTED: "Not requested",
  PENDING: "Pending",
  SENT: "Sent",
  DELIVERY_UNCONFIRMED: "Unconfirmed",
  RECEIVED: "Received",
};
export const ADMIN_CREW_REPORT_DESK_COLORS: Record<string, string> = {
  PANAMAX: "#005474",
  SUPRAMAX: "#1F8475",
  HANDY: "#7C3AED",
  CAPE: "#D96C2F",
  "BABY CAPE": "#B2AD18",
};
export const ADMIN_CREW_REPORT_DESK_FALLBACK_COLORS = ["#0284C7", "#BE185D", "#4A5655", "#92400E"] as const;
export const ADMIN_CREW_REPORT_DESK_LEGEND_FONT_SIZES = [11, 10, 9.5] as const;
export const ADMIN_CREW_REPORT_DESK_LEGEND_NORMAL_COLUMN_GAP_PX = 12;
export const ADMIN_CREW_REPORT_DESK_LEGEND_COMPACT_COLUMN_GAP_PX = 8;
export const ADMIN_CREW_REPORT_DESK_LEGEND_BASE_HEIGHT_PX = 40;
export const ADMIN_CREW_REPORT_DESK_LEGEND_MAX_VISIBLE_ROWS = 3;
export const ADMIN_CREW_REPORT_DESK_BAR_PLOT_DEFAULT_HEIGHT_PX = 144;
export const ADMIN_CREW_REPORT_DESK_BAR_PLOT_MIN_HEIGHT_PX = 120;
export const ADMIN_CREW_REPORT_DESK_LEGEND_MAX_HEIGHT_PX =
  ADMIN_CREW_REPORT_DESK_LEGEND_BASE_HEIGHT_PX +
  ADMIN_CREW_REPORT_DESK_BAR_PLOT_DEFAULT_HEIGHT_PX -
  ADMIN_CREW_REPORT_DESK_BAR_PLOT_MIN_HEIGHT_PX;
export const ADMIN_CREW_REPORT_REPATRIATION_COLUMNS: ReadonlyArray<{
  key: AdminCrewReportRepatriationVesselCountKey;
  label: string;
}> = [
  { key: "below8Months", label: "Below 8m" },
  { key: "from8To11Months", label: "8m to 11m" },
  { key: "from11To13Months", label: "11m to 13m" },
  { key: "above13Months", label: "Above 13m" },
  { key: "totalVesselCount", label: "Total Vessels" },
];
export const ADMIN_CREW_REPORT_OWNER_REPATRIATION_AXIS_INTERVAL = 10;
export const ADMIN_CREW_REPORT_OWNER_REPATRIATION_SEGMENTS: ReadonlyArray<AdminCrewReportOwnerRepatriationSegment> = [
  {
    key: "below8Months",
    label: "Below 8m",
    color: "#4A90E2",
    labelColor: "#111827",
    darkColor: "#93C5FD",
    darkLabelColor: "#111827",
  },
  {
    key: "from8To11Months",
    label: "8m to 11m",
    color: "#005474",
    labelColor: "#FFFFFF",
    darkColor: "#38BDF8",
    darkLabelColor: "#111827",
  },
  {
    key: "from11To13Months",
    label: "11m to 13m",
    color: "#D96C2F",
    labelColor: "#111827",
    darkColor: "#FDBA74",
    darkLabelColor: "#111827",
  },
  {
    key: "above13Months",
    label: "Above 13m",
    color: "#7C3AED",
    labelColor: "#FFFFFF",
    darkColor: "#C4B5FD",
    darkLabelColor: "#111827",
  },
];
export const ADMIN_CREW_REPORT_REPORTING_COLORS = {
  reported: { color: "#1F8475", darkColor: "#5EEAD4" },
  notReported: { color: "#BF0002", darkColor: "#FCA5A5" },
} as const;
export const ADMIN_CREW_REPORT_REPORTED_VESSELS_GAUGE_PATH = "M 18 94 A 82 82 0 0 1 182 94";
export const ADMIN_CREW_REPORT_DATA_COLLECTION_DONUT_RADIUS = 42;
export const ADMIN_CREW_REPORT_STATISTICS_CARD_HEIGHT_CLASS_NAME = "h-[250px]";
export const ADMIN_CREW_REPORT_REPORTING_CARD_CLASS_NAME = `flex ${ADMIN_CREW_REPORT_STATISTICS_CARD_HEIGHT_CLASS_NAME} min-w-0 flex-col border border-[#D8E6E3] bg-white px-3 pb-3 pt-[11px] dark:border-gray-700 dark:bg-gray-800 xl:h-full`;
export const ADMIN_CREW_REPORT_STATISTICS_STATE_CARD_CLASS_NAME =
  "flex min-h-[250px] flex-col border border-[#D8E6E3] bg-white p-4 dark:border-gray-700 dark:bg-gray-800";
export const ADMIN_CREW_REPORT_SECTION_TITLE_CLASS_NAME = "mb-4 text-left text-xs font-semibold uppercase text-green";
export const ADMIN_CREW_REPORT_STATISTICS_PDF_EXPORT_BUTTON_CLASS_NAME =
  "inline-flex h-9 flex-none items-center gap-1.5 whitespace-nowrap px-2 text-left text-xs font-semibold uppercase text-green transition-colors hover:bg-primary-10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green disabled:cursor-not-allowed disabled:opacity-50";
export const ADMIN_CREW_REPORT_TEXT_BUTTON_CLASS_NAME =
  "inline-flex h-9 flex-none items-center justify-center whitespace-nowrap px-2 text-xs font-medium text-green transition-colors hover:bg-primary-10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green disabled:cursor-not-allowed disabled:opacity-50";
export const ADMIN_CREW_REPORT_STATISTICS_TOGGLE_CLASS_NAME = ADMIN_CREW_REPORT_TEXT_BUTTON_CLASS_NAME;
export const ADMIN_CREW_REPORT_STATISTICS_TITLE_CLASS_NAME =
  "text-center text-[12px] font-semibold uppercase leading-3 text-[#4A5655] dark:text-gray-300";
export const ADMIN_CREW_REPORT_STATISTICS_VIEW_LABELS: Record<
  AdminCrewReportStatisticsView,
  {
    toggleButtonLabel: string;
    sectionTitle: string;
    sectionAriaLabel: string;
    personLabel: string;
    byDesksTitle: string;
    byDesksEmptyState: string;
    over11MonthsTitle: string;
    over11MonthsEmptyState: string;
    runningVesselsTitle: string;
    runningVesselsEmptyState: string;
    repatriationTitle: string;
    ownerRepatriationTitle: string;
    overviewAriaLabel: string;
    totalPeopleTitle: string;
    totalPeopleUnit: string;
    totalRunningVesselsTitle: string;
  }
> = {
  crew: {
    toggleButtonLabel: "Women on board",
    sectionTitle: "Crew Information",
    sectionAriaLabel: "Crew statistics",
    personLabel: "crew",
    byDesksTitle: "Crew By Desks",
    byDesksEmptyState: "No crew is available for active vessel categories.",
    over11MonthsTitle: "Crew Over 11 Months",
    over11MonthsEmptyState: "No crew over 11 months is available for active vessel categories.",
    runningVesselsTitle: "Running Vessels",
    runningVesselsEmptyState: "No running Period vessels with vessel types are available.",
    repatriationTitle: "Crew Repatriation",
    ownerRepatriationTitle: "Vessels Per Owner and Crew Repatriation",
    overviewAriaLabel: "Crew overview metrics",
    totalPeopleTitle: "Total Crew On Board",
    totalPeopleUnit: "crew",
    totalRunningVesselsTitle: "Total Running Vessels",
  },
  women: {
    toggleButtonLabel: "Crew on board",
    sectionTitle: "Women Information",
    sectionAriaLabel: "Women On Board statistics",
    personLabel: "women",
    byDesksTitle: "Women By Desks",
    byDesksEmptyState: "No women are available for active vessel categories.",
    over11MonthsTitle: "Women Over 11 Months",
    over11MonthsEmptyState: "No women over 11 months are available for active vessel categories.",
    runningVesselsTitle: "Running Vessels With Women",
    runningVesselsEmptyState: "No running Period vessels with women are available.",
    repatriationTitle: "Women Repatriation",
    ownerRepatriationTitle: "Vessels Per Owner and Women Repatriation",
    overviewAriaLabel: "Women On Board overview metrics",
    totalPeopleTitle: "Total Women On Board",
    totalPeopleUnit: "women",
    totalRunningVesselsTitle: "Total Running Vessels With Women",
  },
};
export const ADMIN_CREW_REPORT_RUNNING_VESSEL_COLUMNS = [
  { key: "bulkRequest", label: "Bulk Request", minWidth: 95, maxWidth: 100 },
  { key: "report", label: "Report", minWidth: 112, maxWidth: 140 },
  { key: "requestStatus", label: "Status", minWidth: 120, maxWidth: 160 },
  { key: "vesselName", label: "Vessel Name", minWidth: 150, maxWidth: 230 },
  { key: "imoNumber", label: "Imo Number", minWidth: 110, maxWidth: 145 },
  { key: "desk", label: "Desk", minWidth: 140, maxWidth: 190 },
  { key: "vesselEmail", label: "Vessel Email", minWidth: 380, maxWidth: 480 },
  { key: "reportDate", label: "Report Date", minWidth: 115, maxWidth: 150 },
  { key: "maxPeriod", label: "Max Period", minWidth: 110, maxWidth: 145 },
  { key: "above11Months", label: "Above 11m", minWidth: 110, maxWidth: 145 },
  { key: "category", label: "Category", minWidth: 115, maxWidth: 155 },
  { key: "hasOverdue", label: "Has Overdue", minWidth: 115, maxWidth: 150 },
  { key: "hasWomen", label: "Has Women", minWidth: 105, maxWidth: 140 },
  { key: "owner", label: "Owner", minWidth: 260, maxWidth: 380 },
  { key: "technicalManager", label: "Technical Manager", minWidth: 300, maxWidth: 420 },
  { key: "registeredOwner", label: "Registered Owner", minWidth: 300, maxWidth: 420 },
  { key: "operator", label: "Operator", minWidth: 260, maxWidth: 380 },
  { key: "beneficialOwner", label: "Beneficial Owner", minWidth: 300, maxWidth: 420 },
] as const;
export const ADMIN_CREW_REPORT_VIEW_COLUMNS = [
  { key: "crewRowNumber", label: "No.", minWidth: 60, maxWidth: 80 },
  { key: "gender", label: "Gender", minWidth: 120, maxWidth: 180 },
  { key: "nationality", label: "Nationality", minWidth: 150, maxWidth: 210 },
  { key: "designationRank", label: "Designation / Rank", minWidth: 160, maxWidth: 220 },
  { key: "contractDurationMonths", label: "Contract duration", minWidth: 190, maxWidth: 260 },
  { key: "signOnDate", label: "Sign on date", minWidth: 200, maxWidth: 270 },
  { key: "changePlannedDate", label: "Change Planned", minWidth: 210, maxWidth: 280 },
  { key: "monthsOnBoard", label: "Months on board", minWidth: 140, maxWidth: 190 },
  { key: "overdue", label: "Overdue", minWidth: 100, maxWidth: 140 },
] as const;

export const CREW_REPORT_UPLOAD_SHEET_NAME = "Crew";
export const CREW_REPORT_UPLOAD_FIRST_DATA_ROW_INDEX = 1;
export const CREW_REPORT_UPLOAD_MAX_LAYOUT_COLUMN_COUNT = 7;
export const CREW_REPORT_UPLOAD_FILE_EXTENSION = ".xlsx";
export const CREW_REPORT_UPLOAD_MAX_FILE_SIZE_MB = 1;
export const CREW_REPORT_UPLOAD_MAX_FILE_SIZE_BYTES = CREW_REPORT_UPLOAD_MAX_FILE_SIZE_MB * 1024 * 1024;
export const CREW_REPORT_UPLOAD_PARSE_TIMEOUT_MS = 15000;
export const EXCEL_DATE_UNIX_EPOCH_SERIAL = 25569;
export const MILLISECONDS_PER_DAY = 86400000;

export const CREW_REPORT_UPLOAD_COLUMNS: AdminCrewReportUploadColumnDefinition[] = [
  { key: "gender", label: "Gender", minWidth: 180, maxWidth: 240 },
  { key: "nationality", label: "Nationality", minWidth: 150, maxWidth: 210 },
  { key: "designationRank", label: "Designation / Rank", minWidth: 160, maxWidth: 220 },
  { key: "contractDurationMonths", label: "Contract duration (months)", minWidth: 190, maxWidth: 260 },
  { key: "signOnDate", label: "Sign on date (mm/dd/yyyy)", minWidth: 200, maxWidth: 270, dateColumn: true },
  {
    key: "changePlannedDate",
    label: "Change Planned (mm/dd/yyyy)",
    minWidth: 210,
    maxWidth: 280,
    dateColumn: true,
  },
];

export const ADMIN_CREW_REPORT_FILE_NAME_PATTERN = /^([0-9]{7})\.xlsx$/i;
export const ADMIN_CREW_REPORT_FILE_NAME_GUIDANCE =
  "Rename the file to the vessel’s seven-digit IMO followed by .xlsx, for example 9713832.xlsx.";
export const ADMIN_CREW_REPORT_HEADER_WHITESPACE_PATTERN = /\s+/g;
export const ADMIN_CREW_REPORT_WORKBOOK_HEADERS = [
  ["gender", "gender (male/female/other/ do not want to reply)"],
  ["nationality"],
  ["designation / rank"],
  ["contract duration in months", "contract duration (months)", "contract duration"],
  ["sign on date format mm/dd/yyyy", "sign on date (mm/dd/yyyy)", "sign on date"],
  ["change planned format mm/dd/yyyy", "change planned (mm/dd/yyyy)", "change planned"],
] as const;
export const ADMIN_CREW_REPORT_WORKBOOK_HEADER_GUIDANCE =
  'Keep the "Crew" sheet and reporting columns in their original order: Gender, Nationality, Designation / Rank, Contract duration, Sign on date, Change Planned. The previous template with a leading Vessel IMO column is also accepted.';
